import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Compass, Trophy, Activity, Users, User } from "lucide-react";
import HeroSection from "@/components/landing/HeroSection";
import LiveMatchStrip from "@/components/landing/LiveMatchStrip";
import CategoryCards from "@/components/landing/CategoryCards";
import PopularSports from "@/components/landing/PopularSports";
import FeaturedFacilities from "@/components/landing/FeaturedFacilities";
import EcosystemSections from "@/components/landing/EcosystemSections";
import InstallPrompt from "@/components/landing/InstallPrompt";

const NAV_TABS = [
  { id: "sec-home", label: "Home", icon: Home },
  { id: "sec-discover", label: "Discover", icon: Compass },
  { id: "sec-play", label: "Play", icon: Trophy },
  { id: "sec-performance", label: "Performance", icon: Activity },
  { id: "sec-community", label: "Community", icon: Users },
  { id: "sec-profile", label: "Profile", icon: User },
];

export default function Landing() {
  const [active, setActive] = useState("sec-home");

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.2, 0.5, 1] });
    NAV_TABS.forEach((t) => { const el = document.getElementById(t.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="glass fixed inset-x-0 top-0 z-50 border-b border-border">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="landing-logo" className="flex items-center">
            <img src="/spora1-logo.webp" alt="SPORA1" className="h-9 w-auto sm:h-10" />
          </Link>
          <nav className="no-scrollbar hidden items-center gap-1 overflow-x-auto md:flex" data-testid="landing-nav-tabs">
            {NAV_TABS.map((t) => {
              const Icon = t.icon;
              const on = active === t.id;
              return (
                <button key={t.id} onClick={() => go(t.id)} data-testid={`landing-tab-${t.label.toLowerCase()}`}
                  className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-all lg:px-4 ${
                    on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                  <Icon style={{ width: 17, height: 17 }} /> <span className="hidden lg:inline">{t.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-btn" className="text-sm font-semibold text-muted-foreground hover:text-foreground">Sign in</Link>
            <Link to="/register" data-testid="nav-register-btn"
              className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-all hover:bg-[#FF8533]">
              Get started
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-16">
        <div id="sec-home"><HeroSection /></div>
        <div id="sec-discover" className="scroll-mt-20"><CategoryCards /><PopularSports /></div>
        <div id="sec-play" className="scroll-mt-20"><LiveMatchStrip /><FeaturedFacilities /></div>
        <EcosystemSections />

        {/* Final CTA */}
        <section id="sec-profile" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-16 sm:px-6">
          <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-foreground p-8 text-background sm:p-12 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Ready to get on the field?</h2>
              <p className="mt-2 text-background/70">Create your SPORA ID and book your first ground, team or trip in minutes.</p>
            </div>
            <Link to="/register" data-testid="cta-register-btn"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:bg-[#FF8533]">
              Create your account <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-8 pb-24 md:pb-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 text-sm text-muted-foreground sm:flex-row sm:px-6">
          <img src="/spora1-logo.webp" alt="SPORA1" className="h-7 w-auto opacity-80" />
          <span>© {new Date().getFullYear()} SPORA1 - Sports Technology & Innovation Foundation.</span>
          <Link to="/help" data-testid="footer-help-link" className="font-medium text-muted-foreground hover:text-primary">Help & Support</Link>
        </div>
      </footer>

      {/* Mobile bottom nav (pre-login) */}
      <nav className="glass fixed inset-x-0 bottom-0 z-40 grid grid-cols-6 border-t border-border md:hidden" data-testid="landing-mobile-nav">
        {NAV_TABS.map((t) => {
          const Icon = t.icon;
          const on = active === t.id;
          return (
            <button key={t.id} onClick={() => go(t.id)} data-testid={`landing-mtab-${t.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${on ? "text-primary" : "text-muted-foreground"}`}>
              <Icon style={{ width: 20, height: 20 }} /> {t.label}
            </button>
          );
        })}
      </nav>

      <InstallPrompt />
    </div>
  );
}
